let l3Worker = null;
let glmWorker = null;
let nextId = 1;
const pending = new Map();

const DECODE_TIMEOUT_MS = 180000;
const L2_POOL_SIZE = 2;

const l2Slots = [];

function bindWorker(worker) {
  worker.onmessage = (event) => {
    const { id, ok, payload, error, progress } = event.data || {};
    if (id == null) return;
    const waiter = pending.get(id);
    if (!waiter) return;
    if (progress) {
      waiter.onProgress?.(progress);
      return;
    }
    pending.delete(id);
    if (ok) waiter.resolve(payload);
    else waiter.reject(new Error(error || "Worker failed"));
  };
  worker.onmessageerror = (err) => {
    const error = err instanceof Error ? err : new Error("Worker message failed");
    rejectWaitersFor(worker, error);
  };
  worker.onerror = (err) => {
    const error = err instanceof Error ? err : new Error(err?.message || "Worker failed");
    rejectWaitersFor(worker, error);
  };
}

function rejectWaitersFor(worker, error) {
  for (const [id, waiter] of pending) {
    if (waiter.worker !== worker) continue;
    pending.delete(id);
    waiter.reject(error);
  }
}

function ensureL2Pool() {
  if (l2Slots.length) return;
  for (let i = 0; i < L2_POOL_SIZE; i++) {
    const worker = new Worker(new URL("./l2Worker.js", import.meta.url), { type: "module" });
    bindWorker(worker);
    l2Slots.push({ worker, inflight: 0 });
  }
}

function pickL2Slot() {
  ensureL2Pool();
  let best = l2Slots[0];
  for (const slot of l2Slots) {
    if (slot.inflight < best.inflight) best = slot;
  }
  return best;
}

function getL3Worker() {
  if (!l3Worker) {
    l3Worker = new Worker(new URL("./l3Worker.js", import.meta.url), { type: "module" });
    bindWorker(l3Worker);
  }
  return l3Worker;
}

function getGlmWorker() {
  if (!glmWorker) {
    glmWorker = new Worker(new URL("../data/glmWorker.js", import.meta.url), { type: "module" });
    bindWorker(glmWorker);
  }
  return glmWorker;
}

function callWorker(worker, message, transfer = [], onProgress) {
  const id = nextId++;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(new Error("Radar decode timed out. Try refreshing."));
    }, DECODE_TIMEOUT_MS);
    pending.set(id, {
      worker,
      resolve: (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      reject: (err) => {
        clearTimeout(timer);
        reject(err);
      },
      onProgress,
    });
    worker.postMessage({ ...message, id }, transfer);
  });
}

function transferBytes(bytes) {
  if (!(bytes instanceof Uint8Array)) {
    const copy = new Uint8Array(bytes);
    return { bytes: copy, transfer: [copy.buffer] };
  }
  if (bytes.byteOffset === 0 && bytes.buffer.byteLength === bytes.byteLength) {
    return { bytes, transfer: [bytes.buffer] };
  }
  const copy = bytes.slice();
  return { bytes: copy, transfer: [copy.buffer] };
}

export function warmL2Worker() {
  try {
    ensureL2Pool();
    return Promise.all(
      l2Slots.map((slot) => callWorker(slot.worker, { warmup: true }).catch(() => {})),
    );
  } catch {
    return Promise.resolve();
  }
}

export function l2WorkerPoolSize() {
  return L2_POOL_SIZE;
}

export function decodeL2InWorker(bytes, options, onProgress) {
  const slot = pickL2Slot();
  slot.inflight += 1;
  const packed = transferBytes(bytes);
  return callWorker(slot.worker, { bytes: packed.bytes, options }, packed.transfer, onProgress)
    .finally(() => {
      slot.inflight -= 1;
    });
}

export function decodeL2SeekStream(options, onProgress) {
  const slot = pickL2Slot();
  slot.inflight += 1;
  const id = nextId++;
  let settled = false;
  const worker = slot.worker;

  const promise = new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      if (settled) return;
      settled = true;
      reject(new Error("Radar decode timed out. Try refreshing."));
    }, DECODE_TIMEOUT_MS);
    pending.set(id, {
      worker,
      resolve: (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      reject: (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      },
      onProgress,
    });
    worker.postMessage({ id, seekStream: true, options });
  }).finally(() => {
    slot.inflight -= 1;
  });

  return {
    promise,
    settled: () => settled,
    push(chunk) {
      if (settled || !chunk?.byteLength) return;
      const copy = chunk instanceof Uint8Array ? chunk.slice() : new Uint8Array(chunk);
      worker.postMessage({ id, chunk: copy }, [copy.buffer]);
    },
    end() {
      if (settled) return;
      worker.postMessage({ id, streamEnd: true });
    },
  };
}

export function decodeL3InWorker(bytes, options) {
  const packed = transferBytes(bytes);
  return callWorker(getL3Worker(), { kind: "polar", bytes: packed.bytes, options }, packed.transfer);
}

export function decodeStormInWorker(bytes, station) {
  const packed = transferBytes(bytes);
  return callWorker(getL3Worker(), { kind: "storm", bytes: packed.bytes, options: { station } }, packed.transfer);
}

export function warmGlmWorker() {
  try {
    callWorker(getGlmWorker(), { warmup: true }).catch(() => {});
  } catch {
    // Worker construction can fail in unsupported browsers.
  }
}

export function parseGlmInWorker(bytes, filename) {
  const packed = transferBytes(bytes);
  return callWorker(getGlmWorker(), { bytes: packed.bytes, filename }, packed.transfer);
}
